import { useState } from "react";
import { Button, DatePicker, Empty, message, Space, Table, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import { AdminService } from "../Services/AdminService";
import { formatDateString } from "../Helpers/DateHelpers";

interface SettlementRow {
    companyId?: number | null;
    companyName?: string | null;
    memberUniqueId?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    amount: number;
    recipientType?: string | null;
    reference?: string | null;
    paymentOrderId?: string | null;
}

interface DisbursementGroup {
    disbursementId: string;
    disbursedAt: string;
    currency?: string | null;
    amount: number;
    rows: SettlementRow[];
}

interface FeeReport {
    disbursements: DisbursementGroup[];
    totalAmount: number;
    fromDate?: string | null;
    toDate?: string | null;
}

const INFO_TEXT =
    "Varje rad är en utbetalning som Ping Payments gjort till OneFronts konto (normalt en gång i veckan). " +
    "Beloppet är OneFronts andel – serviceavgifterna – för de betalningar som ingick i utbetalningen. " +
    "Fäll ut raden för att se vilka betalningar avgiften kommer från. Filtrera på utbetalningsdatum för en bokföringsperiod.";

const emptyReport: FeeReport = { disbursements: [], totalAmount: 0 };

const AdminPingPaymentReport = () => {
    const [report, setReport] = useState<FeeReport>(emptyReport);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);

    const rangeSelected = !!fromDate && !!toDate;

    const fetchReport = async (from: Dayjs, to: Dayjs) => {
        setLoading(true);
        try {
            const result: FeeReport = await AdminService.GetPingPaymentFeeReport(
                from.format("YYYY-MM-DD"),
                to.format("YYYY-MM-DD"),
            );
            setReport(result ?? emptyReport);
            setHasLoaded(true);
        } catch {
            message.error("Kunde inte hämta rapporten");
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        if (!fromDate || !toDate) return;
        setPdfLoading(true);
        try {
            const blob = await AdminService.DownloadPingPaymentFeeReportPdf(
                fromDate.format("YYYY-MM-DD"),
                toDate.format("YYYY-MM-DD"),
            );
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `OneFront_PingPayment_Utbetalningar_${new Date().toISOString().slice(0, 10)}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            message.error("Kunde inte ladda ner PDF");
        } finally {
            setPdfLoading(false);
        }
    };

    const memberName = (row: SettlementRow) =>
        [row.firstName, row.lastName].filter(Boolean).join(" ") || "-";

    const innerColumns: ColumnsType<SettlementRow> = [
        { title: "Förening", dataIndex: "companyName", key: "companyName", render: (v) => v ?? "-" },
        { title: "Medlem", key: "member", render: (_, row) => memberName(row) },
        {
            title: "Referens",
            key: "reference",
            render: (_, row) => row.reference ?? row.paymentOrderId ?? "-",
        },
        {
            title: "Belopp",
            dataIndex: "amount",
            key: "amount",
            align: "right",
            render: (v: number) => v.toFixed(2),
        },
    ];

    const columns: ColumnsType<DisbursementGroup> = [
        {
            title: "Utbetalningsdatum",
            dataIndex: "disbursedAt",
            key: "disbursedAt",
            defaultSortOrder: "descend",
            sorter: (a, b) => new Date(a.disbursedAt).getTime() - new Date(b.disbursedAt).getTime(),
            render: (v) => formatDateString(v) || "-",
        },
        {
            title: "Antal betalningar",
            key: "count",
            align: "right",
            render: (_, group) => group.rows.length,
        },
        { title: "Valuta", dataIndex: "currency", key: "currency", render: (v) => v ?? "SEK" },
        {
            title: "Utbetalt till OneFront",
            dataIndex: "amount",
            key: "amount",
            align: "right",
            sorter: (a, b) => a.amount - b.amount,
            render: (v: number) => <b>{v.toFixed(2)}</b>,
        },
        {
            title: "Referens (Ping)",
            dataIndex: "disbursementId",
            key: "disbursementId",
            render: (v: string) => <span style={{ fontSize: 11, color: "#888" }}>{v}</span>,
        },
    ];

    return (
        <div>
            <p>
                Utbetalningar från Ping Payments till OneFront.{" "}
                <Tooltip title={INFO_TEXT}>
                    <InfoCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
                </Tooltip>
            </p>
            <Space style={{ marginBottom: 16, position: "relative", zIndex: 1 }} wrap>
                <DatePicker value={fromDate} onChange={setFromDate} placeholder="Från datum" allowClear />
                <DatePicker value={toDate} onChange={setToDate} placeholder="Till datum" allowClear />
                <Button
                    type="primary"
                    onClick={() => rangeSelected && fetchReport(fromDate!, toDate!)}
                    loading={loading}
                    disabled={!rangeSelected}
                >
                    Filtrera
                </Button>
                <Button onClick={downloadPdf} loading={pdfLoading} disabled={!hasLoaded}>
                    Ladda ner PDF
                </Button>
            </Space>
            {!hasLoaded ? (
                <Empty
                    style={{ marginTop: 48 }}
                    description="Välj från- och till-datum och klicka på Filtrera för att hämta rapporten."
                />
            ) : (
                <Table
                    rowKey="disbursementId"
                    columns={columns}
                    dataSource={report.disbursements}
                    loading={loading}
                    className="admin-table"
                    style={{ marginTop: 0 }}
                    scroll={{ x: "max-content" }}
                    pagination={{ pageSize: 20 }}
                    expandable={{
                        expandedRowRender: (group) => (
                            <Table
                                rowKey={(row) => `${group.disbursementId}-${row.paymentOrderId}`}
                                columns={innerColumns}
                                dataSource={group.rows}
                                pagination={false}
                                size="small"
                            />
                        ),
                    }}
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={3}>
                                <b>Totalt utbetalt till OneFront</b>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                                <b>{report.totalAmount.toFixed(2)}</b>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={4} />
                        </Table.Summary.Row>
                    )}
                />
            )}
        </div>
    );
};

export default AdminPingPaymentReport;
